/**
 * Chatbot Data Transformation Utilities
 * 
 * Utilities for transforming chatbot data between database format and application format.
 */

import { Question, TriggerRule, TriggerCondition, PostVariableMapping } from '@/types/theme';
import { Json } from '@/integrations/supabase/types';
import { QuestionData, TriggerRuleData } from '@/services/chatbotDataService';

/**
 * Transforms questions from database format to application format
 */
export function transformQuestions(questions: QuestionData[]): Question[] {
  return questions.map((q) => {
    let parsedOptions: { label: string; value: string }[] = [];
    if (q.options) {
      try {
        parsedOptions = typeof q.options === 'string' ? JSON.parse(q.options) : q.options;
        if (!Array.isArray(parsedOptions)) {
          parsedOptions = [];
        }
      } catch (e) {
        console.error('Error parsing options:', e);
        parsedOptions = [];
      }
    }

    let validationRules: any = {};
    let validationType = 'none';

    if (q.responsevalidation) {
      try {
        const validationSettings = typeof q.responsevalidation === 'string' 
          ? JSON.parse(q.responsevalidation) 
          : q.responsevalidation;

        if (validationSettings.validationType) {
          validationType = validationSettings.validationType;
        }

        if (validationSettings.validation_rules) {
          validationRules = {
            ...validationRules,
            ...validationSettings.validation_rules
          };
        }
      } catch (e) {
        console.error('Error parsing validation settings:', e);
      }
    }

    return {
      id: q.id,
      chatbot_id: q.chatbot_id,
      text: q.text,
      type: q.type as any,
      order: q.order,
      options: parsedOptions,
      shortname: q.shortname || '',
      errormessage: q.errormessage || '',
      responsevalidation: q.responsevalidation || '',
      validationType: validationType as any,
      validation_rules: validationRules,
      placeholder: '',
      rich_content: q.rich_content,
      enable_ai_response: q.enable_ai_response || false,
      ai_response_prompt: q.ai_response_prompt || null,
      created_at: q.created_at,
      updated_at: q.updated_at
    };
  });
}

/**
 * Transforms trigger rules from database format to application format
 */
export function transformTriggerRules(triggerRules: TriggerRuleData[]): TriggerRule[] {
  return triggerRules.map((rule) => {
    let conditions: TriggerCondition[] = [];
    let postVariableMappings: PostVariableMapping[] = [];

    // Parse conditions
    if (rule.conditions) {
      try {
        const parsedConditions = typeof rule.conditions === 'string' 
          ? JSON.parse(rule.conditions) 
          : rule.conditions;
        
        if (Array.isArray(parsedConditions)) {
          conditions = parsedConditions;
        }
      } catch (e) {
        console.error('Error parsing trigger conditions:', e);
      }
    }

    // Parse post variable mappings
    if (rule.postvariablemappings) {
      try {
        const parsedMappings = typeof rule.postvariablemappings === 'string'
          ? JSON.parse(rule.postvariablemappings)
          : rule.postvariablemappings;
        
        if (Array.isArray(parsedMappings)) {
          postVariableMappings = parsedMappings;
        }
      } catch (e) {
        console.error('Error parsing post variable mappings:', e);
      }
    }

    return {
      id: rule.id,
      chatbot_id: rule.chatbot_id,
      name: rule.name,
      notes: rule.notes || '',
      conditions: conditions,
      webhookUrl: rule.webhookurl || '',
      postVariableMappings: postVariableMappings,
      active: rule.active,
      created_at: rule.created_at,
      updated_at: rule.updated_at
    };
  });
}


